/* eslint-disable jsx-a11y/anchor-is-valid */
import { Link, Text } from '@react-pdf/renderer';
import sanitizeHtml from 'sanitize-html';
import { getProVesselTypeLabel, ProVesselTypes } from '../models/pro.model';
import styles from '../reporting/pro-report-pdf/pro-report-pdf-styles';

const ALLOWED_HTML_TAGS = ['a', 'br'];
const ALLOWED_HTML_ATTRIBUTES = {
  a: ['href', 'target', 'rel', 'style'],
};
const LINK_REGEX = /<a\s+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;
const NEWLINE_REGEX = /\n/g;
const ALLOWED_HTML_TAGS_REGEX = /<a\s+href=|<br\s*\/?>/i;

/**
 * Returns a comma-separated string of vessel type labels, or 'All' if all types are present except NO_VESSEL_TYPE.
 */
export const getApplicableVesselTypesDisplay = (
  applicableTypes: ProVesselTypes[]
): string => {
  const allTypesExceptNoVesselType = Object.values(ProVesselTypes).filter(
    (type) => type !== ProVesselTypes.NO_VESSEL_TYPE
  );
  const sortedInput = [...applicableTypes].sort();
  const sortedAll = [...allTypesExceptNoVesselType].sort();
  const isAll =
    sortedInput.length === sortedAll.length &&
    sortedInput.every((v, i) => v === sortedAll[i]);
  return isAll
    ? 'All'
    : applicableTypes.map((type) => getProVesselTypeLabel(type)).join(', ');
};

/**
 * Sanitizes a block of HTML, allowing only specified tags and attributes.
 * Inline styles are not validated (see comment for security context).
 */
export const sanitiseBlock = (block: string, allowedTags: string[]) =>
  // NOTE: parseStyleAttributes is set to false because sanitize-html's style parsing
  // relies on postcss, which only works in Node.js. This disables style validation
  // and allows all inline styles. This is safe here because both the HTML and the inline styles
  // are fully controlled and generated by our backend/frontend code—no user input is included.
  // See: https://github.com/apostrophecms/sanitize-html/issues/547
  sanitizeHtml(block, {
    allowedTags,
    allowedAttributes: ALLOWED_HTML_ATTRIBUTES,
    parseStyleAttributes: false,
  });

/**
 * Checks if the content contains allowed HTML tags (<a> or <br>).
 */
export function containsAllowedHtmlTags(
  content: string | null | undefined
): boolean {
  return !!content && ALLOWED_HTML_TAGS_REGEX.test(content);
}

/**
 * Replaces all <a> tags in the input text with a stylized version for email output.
 * Adds color, removes underline, and makes links bold.
 */
export function styliseHtmlLinks(
  text: string | null | undefined
): string | null | undefined {
  if (!text) return text;
  // Replace all <a ...>...</a> tags with stylized version
  return text.replace(
    LINK_REGEX,
    (_match, href, textContent) =>
      `<a href="${href}" target="_blank" rel="noreferrer" style="color: #4bbf7c; text-decoration: none; font-weight: bold;">${textContent}</a>`
  );
}

/**
 * Converts all newlines (\n) in the input string to <br> tags for HTML output.
 */
export function formatNewlinesAsHtmlBreaks(
  input: string | null | undefined
): string | null | undefined {
  if (input == null) return input;
  return input.replace(NEWLINE_REGEX, '<br />');
}

/**
 * Applies HTML formatting helpers: converts newlines to <br>, stylizes links, and sanitizes the result.
 * Used for safe email HTML rendering.
 */
export function handleHtmlStyling(
  text: string | null | undefined
): string | null | undefined {
  const result = styliseHtmlLinks(formatNewlinesAsHtmlBreaks(text));
  const safeHtml = result ? sanitiseBlock(result, ALLOWED_HTML_TAGS) : result;
  return safeHtml;
}

/**
 * Formats the input string for safe HTML email rendering and determines if it contains allowed HTML tags.
 * This helper applies all email-safe formatting (newlines to <br>, stylized links, sanitization) and
 * returns both the formatted HTML string and a boolean indicating if it contains allowed tags (e.g., <a>, <br>).
 *
 * @param content - The raw string content to format and check.
 * @returns An object with the formatted HTML string and a boolean flag for allowed tag presence.
 */
export function getFormattedHtmlAndIsSafe(content: string) {
  const formatted = handleHtmlStyling(content);
  const isSafe = containsAllowedHtmlTags(formatted);
  return { formatted, isSafe };
}

/**
 * Splits a line of text into an array of <Text> and <Link> elements for PDF rendering.
 * Handles multiple links and plain text segments.
 */
function renderPdfLineWithLinks(
  line: string,
  keyPrefix: string,
  keyStart: number = 0
): React.ReactNode[] {
  const linkRegex = LINK_REGEX;
  let lastIndex = 0;
  let key = keyStart;
  const lineParts: React.ReactNode[] = [];
  for (let m = linkRegex.exec(line); m !== null; m = linkRegex.exec(line)) {
    if (m.index > lastIndex) {
      lineParts.push(
        <Text key={`${keyPrefix}-t${(key += 1)}`}>
          {line.slice(lastIndex, m.index)}
        </Text>
      );
    }
    lineParts.push(
      <Link key={`${keyPrefix}-l${(key += 1)}`} style={styles.link} src={m[1]}>
        {m[2]}
      </Link>
    );
    lastIndex = linkRegex.lastIndex;
  }
  if (lastIndex < line.length) {
    lineParts.push(
      <Text key={`${keyPrefix}-t${(key += 1)}`}>{line.slice(lastIndex)}</Text>
    );
  }
  return lineParts;
}

/**
 * Renders an array of lines as <Text> elements for PDF, optionally prepending a header to the first line.
 */
function renderPdfLinesWithOptionalHeader(
  lines: string[],
  prependSubHeader?: string
): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let key = 0;
  for (let lineIdx = 0; lineIdx < lines.length; lineIdx += 1) {
    const line = lines[lineIdx];
    const lineParts = renderPdfLineWithLinks(line, `line${lineIdx}`, key);
    if (lineIdx === 0 && prependSubHeader) {
      result.push(
        <Text key={`line${lineIdx}`}>
          <Text style={{ fontWeight: 700 }}>{prependSubHeader}</Text>{' '}
          {lineParts}
        </Text>
      );
    } else {
      result.push(<Text key={`line${lineIdx}`}>{lineParts}</Text>);
    }
    key += lineParts.length;
  }
  return result;
}

/**
 * Formats and renders multi-line, possibly HTML-rich text for PDF output.
 * Splits by newlines, renders links, and optionally prepends a header to the first line.
 * If textContent is empty, renders the provided fallback text.
 */
export function formatAndRenderPdfTextContentHtml(
  textContent: string | null | undefined,
  fallBackTextContent: string | null | undefined,
  options?: { prependSubHeader?: string }
) {
  if (!textContent) return [<Text key="none">{fallBackTextContent}</Text>];

  const lines = textContent.split('\n');
  const renderedLines = renderPdfLinesWithOptionalHeader(
    lines,
    options?.prependSubHeader
  );
  return renderedLines;
}
